import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ThemeMode } from '../theme';

/**
 * Theme mode = the user's choice: 'light' | 'dark' | 'system'.
 * 'system' means "follow OS prefers-color-scheme". We resolve it to a concrete
 * 'light' or 'dark' for the actual theme.
 *
 * Persistence: localStorage key 'mstms_theme_mode' (mirrors how language is stored).
 */
export type ThemeModePreference = ThemeMode | 'system';

interface ThemeModeContextValue {
  /** The user's stored preference (may be 'system'). */
  preference: ThemeModePreference;
  /** The currently-applied concrete mode after resolving 'system'. */
  mode: ThemeMode;
  /** Update the stored preference. Triggers a theme rebuild. */
  setPreference: (next: ThemeModePreference) => void;
  /** Convenience: flip between light and dark; 'system' becomes the opposite of current OS. */
  toggle: () => void;
}

const STORAGE_KEY = 'mstms_theme_mode';

const ThemeModeContext = createContext<ThemeModeContextValue>({
  preference: 'system',
  mode: 'light',
  setPreference: () => {},
  toggle: () => {},
});

function readStored(): ThemeModePreference {
  if (typeof window === 'undefined') return 'system';
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  return 'system';
}

function detectSystemMode(): ThemeMode {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Provider that owns the theme-mode preference and the resolved concrete mode.
 *
 * It seeds `preference` from localStorage and `systemMode` from the OS, keeps
 * `systemMode` in sync via a `prefers-color-scheme` media-query listener, and
 * derives the applied `mode` (resolving 'system' to the OS value). Wrap the app
 * once and feed `mode` into the MUI theme builder.
 *
 * @param children React subtree that will have access to the theme-mode context.
 */
export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemeModePreference>(() => readStored());
  const [systemMode, setSystemMode] = useState<ThemeMode>(() => detectSystemMode());

  // Watch the OS preference so 'system' stays in sync.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemMode(e.matches ? 'dark' : 'light');
    if (mq.addEventListener) {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    // Older browsers
    mq.addListener(handler);
    return () => mq.removeListener(handler);
  }, []);

  const setPreference = useCallback((next: ThemeModePreference) => {
    setPreferenceState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore quota / SSR */
    }
  }, []);

  const mode: ThemeMode = preference === 'system' ? systemMode : preference;

  const toggle = useCallback(() => {
    setPreference(mode === 'dark' ? 'light' : 'dark');
  }, [mode, setPreference]);

  const value = useMemo<ThemeModeContextValue>(
    () => ({ preference, mode, setPreference, toggle }),
    [preference, mode, setPreference, toggle],
  );

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
}

/**
 * Hook to consume the theme-mode context. Use inside <ThemeModeProvider> to read
 * the user's `preference` / applied `mode` and to change it via `setPreference`
 * or `toggle` (e.g. from a theme switcher in the app bar).
 *
 * @returns The current {@link ThemeModeContextValue}.
 */
export function useThemeMode(): ThemeModeContextValue {
  return useContext(ThemeModeContext);
}
